import React from 'react';
import { RED, INK, GREY, LIGHT, TEAL, BORDER, wrap, CtaBand } from '../seo/SeoUI';

// /property-managers — the B2B landing page our outreach (calls, emails, PMI/NARPM
// contacts) can point to. Claims mirror the approved PM pitch: first job $0 BestHand
// fees, dispatch in minutes, background-checked pros, upfront itemized pricing, one
// bill, end-of-job photos, damage protection. Lead form posts to /property-managers/lead
// (server-rendered confirmation; the lead lands in the admin inbox).

const PERKS = [
  ['⚡', 'Dispatch any job in minutes', 'Leak, outlet, lockout, turnover — post it once and a background-checked pro picks it up, often the same day.'],
  ['🧾', 'Upfront, itemized pricing', 'You see the price before you book. Final invoices are itemized — labor, approved extra time, materials — with end-of-job photos.'],
  ['🏢', 'One bench, all your units', 'Stop juggling ten trade contacts. One platform covers plumbing, electrical, HVAC help, cleaning, turnovers and more.'],
  ['🛡️', 'Verified & protected', 'Every pro is identity-verified and background-checked, and eligible jobs carry damage protection.'],
  ['💳', 'One simple bill', 'Card on file, per-job receipts, monthly visibility — no lead fees, no subscriptions, a simple 10% platform fee.'],
  ['📸', 'Proof of work, every time', 'Completion photos and itemized invoices on each job — the paper trail your owners expect.'],
];

const STEPS = [
  ['Tell us about your portfolio', 'Share your unit count and area below — we set your account up for you.'],
  ['Post your first job free', 'Your first job carries $0 BestHand fees — you only pay the pro’s rate.'],
  ['Dispatch & track everything', 'Book in minutes, follow the job live, get photos + an itemized invoice at the end.'],
];

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13.5, fontWeight: 700, color: INK }}>
      {label}
      {children}
    </label>
  );
}

const inputStyle = {
  padding: '12px 14px',
  borderRadius: 10,
  border: `1px solid ${BORDER}`,
  fontSize: 15,
  color: INK,
  background: '#fff',
  outline: 'none',
  fontWeight: 400,
  width: '100%',
};

function PropertyManagers() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'BestHand for Property Managers',
      description: 'On-demand maintenance bench for property managers — background-checked pros, upfront itemized pricing, one simple bill.',
      provider: { '@type': 'Organization', name: 'BestHand' },
      areaServed: 'United States',
    },
  ];

  return (
    <div style={{ background: '#fff' }}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      {/* Hero */}
      <section style={{ background: `linear-gradient(160deg, #1B2430 0%, #232F3E 60%, #2A2330 100%)`, color: '#fff' }}>
        <div style={{ ...wrap, padding: '72px 20px 64px', display: 'flex', flexWrap: 'wrap', gap: 40, alignItems: 'center' }}>
          <div style={{ flex: '1 1 460px', minWidth: 300 }}>
            <span style={{ display: 'inline-block', fontSize: 12.5, fontWeight: 800, letterSpacing: '.8px', textTransform: 'uppercase', color: '#7FE7DC', marginBottom: 14 }}>For property managers</span>
            <h1 style={{ fontSize: 'clamp(30px,4.2vw,44px)', fontWeight: 800, lineHeight: 1.12, margin: '0 0 16px' }}>
              A maintenance team for your units — that actually shows up.
            </h1>
            <p style={{ fontSize: 17, lineHeight: 1.65, color: 'rgba(255,255,255,.88)', margin: '0 0 22px', maxWidth: 560 }}>
              Stop chasing handymen for every leak, outlet or turnover. BestHand gives property managers one on-demand bench of background-checked pros — booked in minutes, upfront pricing, one simple bill.
            </p>
            <div style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
              <a href="#pm-lead-form" style={{ display: 'inline-block', padding: '14px 26px', borderRadius: 12, background: RED, color: '#fff', fontWeight: 800, fontSize: 16, textDecoration: 'none' }}>Get your first job free →</a>
              <span style={{ fontSize: 13.5, color: 'rgba(255,255,255,.75)' }}>$0 BestHand fees on your first job — you only pay the pro.</span>
            </div>
          </div>
          <div style={{ flex: '1 1 300px', minWidth: 260 }}>
            <div style={{ background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,255,255,.16)', borderRadius: 18, padding: '24px 26px', display: 'grid', gap: 14 }}>
              {[
                ['Same-week response', 'many jobs handled the day they’re posted'],
                ['Background-checked pros', 'identity-verified before they reach a unit'],
                ['Itemized invoices + photos', 'a clean paper trail for your owners'],
                ['No lead fees', 'simple 10% platform fee, first job $0 fees'],
              ].map(([t, d]) => (
                <div key={t} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <span aria-hidden="true" style={{ color: '#7FE7DC', fontWeight: 900 }}>✓</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{t}</div>
                    <div style={{ fontSize: 13.5, color: 'rgba(255,255,255,.75)' }}>{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Perks grid */}
      <section style={{ ...wrap, padding: '56px 20px 20px' }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.7px', textTransform: 'uppercase', color: RED }}>Why property managers switch</span>
        <h2 style={{ fontSize: 28, fontWeight: 800, margin: '6px 0 24px', color: INK }}>Built for portfolios, not one-off favors</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 18 }}>
          {PERKS.map(([icon, t, d]) => (
            <div key={t} style={{ border: `1px solid ${BORDER}`, borderRadius: 16, padding: '22px 22px', background: '#fff' }}>
              <div aria-hidden="true" style={{ fontSize: 26, marginBottom: 10 }}>{icon}</div>
              <h3 style={{ fontSize: 17.5, fontWeight: 800, margin: '0 0 8px', color: INK }}>{t}</h3>
              <p style={{ fontSize: 14.5, color: GREY, margin: 0, lineHeight: 1.6 }}>{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Steps */}
      <section style={{ background: LIGHT, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, marginTop: 36 }}>
        <div style={{ ...wrap, padding: '48px 20px' }}>
          <h2 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 24px', color: INK }}>How it works</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 18 }}>
            {STEPS.map(([t, d], i) => (
              <div key={t} style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 16, padding: '22px 22px' }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: RED, color: '#fff', display: 'grid', placeItems: 'center', fontWeight: 800, fontSize: 14, marginBottom: 12 }}>{i + 1}</div>
                <h3 style={{ fontSize: 16.5, fontWeight: 800, margin: '0 0 6px', color: INK }}>{t}</h3>
                <p style={{ fontSize: 14, color: GREY, margin: 0, lineHeight: 1.6 }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead form */}
      <section id="pm-lead-form" style={{ ...wrap, padding: '52px 20px 56px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 36 }}>
          <div style={{ flex: '1 1 320px', minWidth: 280 }}>
            <span style={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '.7px', textTransform: 'uppercase', color: TEAL }}>Get started</span>
            <h2 style={{ fontSize: 27, fontWeight: 800, margin: '6px 0 12px', color: INK }}>Tell us about your portfolio</h2>
            <p style={{ fontSize: 15, color: GREY, lineHeight: 1.65, margin: '0 0 18px', maxWidth: 460 }}>
              We’ll set up your account, walk you through your first dispatch, and apply the founder offer: <b style={{ color: INK }}>$0 BestHand fees on your first job</b>. Usually a same-day reply.
            </p>
            <p style={{ fontSize: 14.5, color: GREY, lineHeight: 1.7, margin: 0 }}>
              Prefer to talk? Call <a href="tel:+12674151234" style={{ color: RED, fontWeight: 700, textDecoration: 'none' }}>(267) 415-1234</a><br />
              or email <a href="mailto:contact@besthandapp.com" style={{ color: RED, fontWeight: 700, textDecoration: 'none' }}>contact@besthandapp.com</a>
            </p>
          </div>
          <form method="post" action="/property-managers/lead" style={{ flex: '1 1 380px', minWidth: 300, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 18, padding: '26px 26px', display: 'grid', gap: 14 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Your name"><input name="name" required maxLength={120} style={inputStyle} placeholder="Jane Smith" /></Field>
              <Field label="Company"><input name="company" required maxLength={160} style={inputStyle} placeholder="Acme Property Mgmt" /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Work email"><input type="email" name="email" required maxLength={254} style={inputStyle} placeholder="jane@acmepm.com" /></Field>
              <Field label="Phone"><input type="tel" name="phone" maxLength={30} style={inputStyle} placeholder="(215) 555-0123" /></Field>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Units under management">
                <select name="units" style={{ ...inputStyle, appearance: 'auto' }} defaultValue="1-25">
                  <option value="1-25">1–25</option>
                  <option value="26-100">26–100</option>
                  <option value="101-500">101–500</option>
                  <option value="500+">500+</option>
                </select>
              </Field>
              <Field label="Main area"><input name="area" maxLength={120} style={inputStyle} placeholder="Philadelphia, PA" /></Field>
            </div>
            <Field label="What do you need most? (optional)">
              <textarea name="message" rows={3} maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }} placeholder="Turnovers, plumbing calls, lockouts…" />
            </Field>
            <button type="submit" style={{ padding: '14px 24px', borderRadius: 12, background: RED, color: '#fff', fontWeight: 800, fontSize: 16, border: 'none', cursor: 'pointer' }}>
              Claim the founder offer →
            </button>
            <p style={{ fontSize: 12, color: GREY, margin: 0, lineHeight: 1.5 }}>We’ll only use this to set up your account and reply — no spam, ever.</p>
          </form>
        </div>
      </section>

      <CtaBand title="Your next work order, handled" subtitle="One bench of background-checked pros for every unit — first job on us." ctaText="Get your first job free" ctaHref="#pm-lead-form" />
    </div>
  );
}

export default PropertyManagers;
